import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api/client";
import type { BillingCycle, Plan, Usage } from "../types";
import { readCheckoutFlag, clearCheckoutFlag } from "../lib/checkoutFlag";
import { track } from "../lib/analytics";
import GenerationLoader from "../components/GenerationLoader";

type PaidPlan = "pratica" | "maestro";

const PLANS: {
  id: PaidPlan;
  name: string;
  tagline: string;
  monthly: string;
  annualPerMo: string;
  annualTotal: string;
  featured?: boolean;
  plus?: string;
  features: string[];
  limits: string[];
}[] = [
  {
    id: "pratica",
    name: "Pratica",
    tagline: "Real spoken Italian practice with feedback you can track.",
    monthly: "17.90",
    annualPerMo: "16.41",
    annualTotal: "196.90",
    featured: true,
    features: [
      "Real-time voice conversation with Elena",
      "Live pronunciation feedback",
      "Automatic error tracking, reviewed after each session",
      "Personalized exercises generated from every session",
      "Progress dashboard, session reports & vocabulary tracking",
      "Adapts to your CEFR level",
    ],
    limits: ["Up to 12 sessions / month", "10 minutes per session"],
  },
  {
    id: "maestro",
    name: "Maestro",
    tagline: "For serious learners who need Italian for a specific context.",
    monthly: "29.90",
    annualPerMo: "27.41",
    annualTotal: "328.90",
    plus: "Everything in Pratica, plus",
    features: [
      "Upload your own materials, practice Italian for your field",
      "In-conversation correction of all error types, not just after",
      "Higher session allowance for daily practice",
    ],
    limits: ["Up to 20 sessions / month", "10 minutes per session"],
  },
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status");

  const flag = useMemo(() => readCheckoutFlag(), []);
  const [annual, setAnnual] = useState<boolean>(flag?.billing !== "monthly");
  const [usage, setUsage] = useState<Usage | null>(null);
  const [pending, setPending] = useState<PaidPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [activationTimedOut, setActivationTimedOut] = useState(false);
  // Show the full-screen "Activating…" loader only while we're still waiting on the
  // webhook; once the poll gives up, fall back to the normal page + a notice.
  const activating = status === "success" && !activationTimedOut;
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    api.getUsage().then((u) => {
      setUsage(u);
      // Tagged with sessions_used: the signup → upgrade_page_view → checkout
      // funnel is only readable if you can see how much practice preceded it.
      // Skipped on the post-Checkout return, which isn't a fresh page view.
      if (status !== "success") {
        track("upgrade_page_view", { sessions_used: u.used, plan: u.plan });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returned from a successful Stripe Checkout: the webhook flips the plan
  // asynchronously, so poll usage until it lands, then drop into the app. Cap the
  // wait — if the webhook is slow or failed, give up rather than spin the
  // "Activating…" screen forever, and surface a recoverable message instead.
  useEffect(() => {
    if (status !== "success") return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 15; // ~30s at 2s intervals
    const tick = async () => {
      try {
        const u = await api.getUsage();
        if (cancelled) return;
        setUsage(u);
        if (u.plan !== "trial") {
          clearCheckoutFlag();
          navigate("/", { replace: true });
          return;
        }
      } catch {
        /* keep polling */
      }
      if (cancelled) return;
      if (++attempts >= MAX_ATTEMPTS) {
        setActivationTimedOut(true);
        setNotice(
          "Payment received — your plan is taking a moment to activate. Refresh in a minute, or use Manage billing if it persists.",
        );
        return;
      }
      pollRef.current = window.setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [status, navigate]);

  // Returned from a Session Pack checkout: confirm + refetch (webhook is async),
  // then drop the ?status param so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (status !== "pack_success" && status !== "pack_cancel") return;
    if (status === "pack_success") {
      setNotice("Session pack added — credits will appear in a moment.");
      let n = 0;
      const poll = () => {
        api.getUsage().then((u) => {
          setUsage(u);
          if (!u.pack_credits && n++ < 5) window.setTimeout(poll, 2000);
        }).catch(() => {});
      };
      poll();
    }
    searchParams.delete("status");
    setSearchParams(searchParams, { replace: true });
  }, [status, searchParams, setSearchParams]);

  const startCheckout = async (plan: PaidPlan) => {
    setPending(plan);
    setError(null);
    try {
      const billing: BillingCycle = annual ? "annual" : "monthly";
      const { url } = await api.createCheckout(plan, billing);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not start checkout. Please try again.");
      setPending(null);
    }
  };

  const openPortal = async () => {
    setError(null);
    try {
      const { url } = await api.openBillingPortal("/upgrade");
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not open the billing portal.");
    }
  };

  const buyPack = async () => {
    setError(null);
    try {
      const { url } = await api.buySessionPack("/upgrade");
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not start the session-pack checkout.");
    }
  };

  if (activating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <GenerationLoader
          messages={["Confirming your payment...", "Activating your plan...", "Almost there..."]}
          subtitle="This only takes a moment."
        />
      </div>
    );
  }

  const currentPlan: Plan = usage?.plan ?? "trial";
  const isPaid = currentPlan !== "trial";

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-8">
        <p className="section-label mb-2">Plans</p>
        <h1 className="font-display text-3xl text-ink mb-2">Keep practicing with Elena.</h1>
        <p className="text-text-secondary text-sm max-w-2xl">
          {isPaid
            ? "You're on a paid plan. Switch plans or manage billing anytime."
            : "Your free trial is active. Upgrade whenever you're ready, no rush."}
        </p>
      </div>

      {notice && (
        <div className="mb-6 rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
          {notice}
        </div>
      )}
      {status === "cancel" && (
        <div className="mb-6 rounded-xl border border-border-soft bg-surface2 px-4 py-3 text-sm text-text-secondary">
          Checkout canceled, no charge was made. You can pick a plan whenever you like.
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-400">
          {error}
        </div>
      )}

      {/* Billing toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex items-center gap-1 rounded-full border border-border-soft bg-surface p-1">
          <button
            type="button"
            onClick={() => setAnnual(false)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              !annual ? "bg-ink text-cream" : "text-text-secondary hover:text-ink"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setAnnual(true)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              annual ? "bg-ink text-cream" : "text-text-secondary hover:text-ink"
            }`}
          >
            Annual <span className="text-success">1 month free</span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {PLANS.map((p) => {
          const isCurrent = currentPlan === p.id;
          return (
            <div
              key={p.id}
              className="card p-6 flex flex-col"
              style={
                p.featured
                  ? { borderColor: "color-mix(in srgb, #e8a93c 50%, transparent)" }
                  : undefined
              }
            >
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-semibold text-ink">{p.name}</h2>
                {p.featured && (
                  <span
                    className="text-[0.625rem] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "rgba(232,169,60,0.16)", color: "#e8a93c" }}
                  >
                    Most popular
                  </span>
                )}
                {isCurrent && (
                  <span className="text-[0.625rem] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-success/20 text-success">
                    Current
                  </span>
                )}
              </div>
              <p className="text-sm text-text-secondary mb-4">{p.tagline}</p>

              <div className="flex items-end gap-2">
                <span className="text-3xl font-semibold text-ink">€{annual ? p.annualPerMo : p.monthly}</span>
                <span className="text-text-tertiary text-sm mb-1">/ month</span>
                {annual && (
                  <span className="text-success text-xs font-semibold mb-1.5">1 month free</span>
                )}
              </div>
              <p className="text-xs text-text-tertiary mt-1 mb-4">
                {annual ? `€${p.annualTotal} today · 12 months of practice` : "billed monthly, cancel anytime"}
              </p>

              {isCurrent ? (
                <button onClick={openPortal} className="btn-quiet w-full">
                  Manage billing
                </button>
              ) : isPaid ? (
                <button onClick={openPortal} className="btn-primary w-full">
                  Switch to {p.name}
                </button>
              ) : (
                <button
                  onClick={() => startCheckout(p.id)}
                  disabled={pending !== null}
                  className="btn-primary w-full"
                >
                  {pending === p.id ? "Redirecting…" : `Choose ${p.name}`}
                </button>
              )}

              <hr className="border-border-soft my-5" />
              {p.plus && <p className="text-xs font-semibold text-text-tertiary mb-2">{p.plus}</p>}
              <ul className="flex flex-col gap-2 mb-4">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-success mt-0.5">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-tertiary">
                {p.limits.map((l) => (
                  <span key={l}>{l}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {isPaid ? (
        <div className="card p-5 mt-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-ink">Need more sessions this month?</p>
            <p className="text-xs text-text-secondary mt-0.5">
              A Session Pack adds 10 sessions for €16.90. They never expire.
              {usage?.pack_credits ? ` You have ${usage.pack_credits} left.` : ""}
            </p>
          </div>
          <button onClick={buyPack} className="btn-primary">Buy a session pack</button>
        </div>
      ) : (
        <p className="text-center text-xs text-text-secondary mt-8">
          Need more than your monthly allowance? Session Packs add 10 extra sessions for €16.90, they never
          expire, on any paid plan.
        </p>
      )}

      <div className="text-center mt-6">
        <button onClick={() => navigate("/")} className="text-sm text-text-secondary hover:text-ink cursor-pointer">
          ← Back to practice
        </button>
      </div>
    </div>
  );
}
